// Vencimiento por inactividad y absoluto del HCE en el navegador (MT-047, H-018). El servidor manda (lib/hce-sesion-vencimiento.ts: 30 min de inactividad y
// 12 h absolutas por defecto, configurables); esto avisa antes y cierra la sesión de forma limpia en vez de dejar que la persona descubra un 401 a mitad
// de una nota. Los límites efectivos los entrega el servidor en la respuesta de `auth/me` (`hceSesion`); sin ellos se usan los defectos.
//
// - La última actividad se guarda en localStorage: varias pestañas del mismo médico cuentan como UNA.
// - El latido a `auth/me` lleva la cabecera de actividad de usuario y solo sale si hubo actividad real: es lo ÚNICO que renueva la inactividad en el
//   servidor (el tráfico de fondo —Sala, telemedicina, agenda— no la renueva).
// - Pestañas en segundo plano: Chrome frena los temporizadores; se reevalúa al volver a ser visible y al recuperar el foco.
// - Si una pestaña cierra la sesión, las demás cierran también (evento `storage`).
import { useEffect, useRef, useState } from 'react';

export const INACTIVIDAD_LIMITE_MS = 30 * 60_000;
export const INACTIVIDAD_AVISO_MS = 60_000;
export const ABSOLUTO_LIMITE_MS = 12 * 3_600_000;
/** El aviso del vencimiento absoluto (no se puede prolongar) sale con más margen: da tiempo de terminar y guardar una nota. */
export const ABSOLUTO_AVISO_MS = 10 * 60_000;
/** Cada cuánto, como máximo, se avisa al servidor de que hubo actividad (sin peticiones el servidor creería que la persona se fue). */
export const LATIDO_MS = 5 * 60_000;
export const CABECERA_ACTIVIDAD = 'X-Actividad-Usuario';
const CLAVE_ACTIVIDAD = 'hce-ultima-actividad';
const CLAVE_CIERRE = 'hce-sesion-cerrada';
const EVENTOS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;
const GRANULARIDAD_MS = 1000;

export type EstadoInactividad = 'activo' | 'aviso' | 'vencido';
export type LimitesServidor = { inactividadMs: number; absolutaMs: number; restanteAbsolutoMs: number };

/** Función pura: el estado según cuánto hace de la última actividad. */
export function estadoDeInactividad(ultimaActividad: number, ahora: number, limiteMs = INACTIVIDAD_LIMITE_MS, avisoMs = INACTIVIDAD_AVISO_MS): EstadoInactividad {
  const inactivo = ahora - ultimaActividad;
  if (inactivo >= limiteMs) return 'vencido';
  if (inactivo >= limiteMs - avisoMs) return 'aviso';
  return 'activo';
}

/** Función pura: estado respecto del vencimiento absoluto (`hasta` = instante en que el servidor mata la sesión; `null` = aún no se conoce). */
export function estadoAbsoluto(hasta: number | null, ahora: number, avisoMs = ABSOLUTO_AVISO_MS): EstadoInactividad {
  if (hasta === null) return 'activo';
  if (ahora >= hasta) return 'vencido';
  if (ahora >= hasta - avisoMs) return 'aviso';
  return 'activo';
}

/** Segundos que faltan para el cierre (nunca negativo). */
export function segundosRestantes(ultimaActividad: number, ahora: number, limiteMs = INACTIVIDAD_LIMITE_MS): number {
  return Math.max(0, Math.ceil((limiteMs - (ahora - ultimaActividad)) / 1000));
}

/** Acepta solo límites numéricos y razonables de `auth/me`; cualquier otra cosa se ignora (quedan los defectos). */
export function limitesDeRespuesta(cuerpo: unknown): LimitesServidor | null {
  const l = (cuerpo as { hceSesion?: Partial<LimitesServidor> } | null)?.hceSesion;
  if (!l) return null;
  const ok = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n >= 0;
  if (!ok(l.inactividadMs) || l.inactividadMs < 60_000 || !ok(l.absolutaMs) || !ok(l.restanteAbsolutoMs)) return null;
  return { inactividadMs: l.inactividadMs, absolutaMs: l.absolutaMs, restanteAbsolutoMs: l.restanteAbsolutoMs };
}

function leerActividad(): number | null {
  try {
    const n = Number(localStorage.getItem(CLAVE_ACTIVIDAD));
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function guardarActividad(ahora: number): void {
  try {
    localStorage.setItem(CLAVE_ACTIVIDAD, String(ahora));
  } catch {
    /* sin almacenamiento: la cuenta queda solo en memoria de esta pestaña */
  }
}

export function olvidarActividad(): void {
  try {
    localStorage.removeItem(CLAVE_ACTIVIDAD);
  } catch {
    /* nada que olvidar */
  }
}

/** Avisa a las demás pestañas de que esta cerró la sesión (ellas la cierran al ver el cambio). */
export function anunciarCierre(ahora = Date.now()): void {
  try {
    localStorage.setItem(CLAVE_CIERRE, String(ahora));
  } catch {
    /* sin almacenamiento: las demás pestañas se cierran solas por su propio reloj */
  }
}

type Opciones = {
  activo: boolean;
  /** `local` = venció aquí; `otra-pestana` = otra pestaña ya cerró la sesión. */
  onVencido: (origen: 'local' | 'otra-pestana') => void;
  /** Latido al servidor (`auth/me` con la cabecera de actividad). Puede devolver los límites efectivos (`hceSesion`). */
  onLatido?: () => Promise<unknown> | void;
  limiteMs?: number;
  avisoMs?: number;
  /** Reloj inyectable (pruebas). */
  ahora?: () => number;
  intervaloMs?: number;
};

/**
 * Vigila la inactividad y el vencimiento absoluto mientras `activo`. Devuelve el estado, el motivo del aviso y `seguir()` (actividad explícita: lo usa el
 * botón del aviso de inactividad). Al vencer llama UNA vez a `onVencido`. No toca formularios: avisa, y el cierre llega tras el aviso.
 */
export function useInactividad({ activo, onVencido, onLatido, limiteMs: limiteInicial = INACTIVIDAD_LIMITE_MS, avisoMs = INACTIVIDAD_AVISO_MS, ahora = Date.now, intervaloMs = 1000 }: Opciones) {
  const [estado, setEstado] = useState<EstadoInactividad>('activo');
  const [absoluto, setAbsoluto] = useState<'activo' | 'aviso'>('activo');
  const [segundosAbsoluto, setSegundosAbsoluto] = useState(0);
  const [segundos, setSegundos] = useState(0);
  const ultima = useRef<number>(0);
  const ultimoLatido = useRef<number>(0);
  const hayActividadSinLatido = useRef(false);
  const vencido = useRef(false);
  const limite = useRef(limiteInicial);
  const absolutoHasta = useRef<number | null>(null);
  const cb = useRef({ onVencido, onLatido });
  cb.current = { onVencido, onLatido };
  const evaluar = useRef((): void => undefined);
  const registrar = useRef((): void => undefined);

  const latir = (t: number) => {
    hayActividadSinLatido.current = false;
    ultimoLatido.current = t;
    const r = cb.current.onLatido?.();
    if (r && typeof (r as Promise<unknown>).then === 'function') {
      (r as Promise<unknown>).then(
        (cuerpo) => {
          const l = limitesDeRespuesta(cuerpo);
          if (l) {
            limite.current = l.inactividadMs;
            absolutoHasta.current = ahora() + l.restanteAbsolutoMs;
          }
        },
        () => undefined,
      );
    }
  };

  registrar.current = () => {
    if (vencido.current) return; // tras el cierre ya no se escribe actividad (otra pestaña no debe seguir creyendo que hay alguien)
    const t = ahora();
    if (t - ultima.current < GRANULARIDAD_MS) return;
    ultima.current = t;
    guardarActividad(t);
    hayActividadSinLatido.current = true;
  };

  evaluar.current = () => {
    if (vencido.current) return;
    const t = ahora();
    const otra = leerActividad(); // actividad de OTRA pestaña
    if (otra && otra > ultima.current) ultima.current = otra;
    const porInactividad = estadoDeInactividad(ultima.current, t, limite.current, avisoMs);
    const porAbsoluto = estadoAbsoluto(absolutoHasta.current, t);
    if (porInactividad === 'vencido' || porAbsoluto === 'vencido') {
      vencido.current = true;
      setEstado('vencido');
      cb.current.onVencido('local');
      return;
    }
    // Los dos avisos son independientes: cerrar el del absoluto no apaga el de inactividad (y viceversa).
    setAbsoluto(porAbsoluto === 'aviso' ? 'aviso' : 'activo');
    setSegundosAbsoluto(Math.max(0, Math.ceil(((absolutoHasta.current ?? t) - t) / 1000)));
    setEstado(porInactividad);
    setSegundos(segundosRestantes(ultima.current, t, limite.current));
    const cadaMs = Math.min(LATIDO_MS, limite.current / 4);
    if (porInactividad === 'activo' && hayActividadSinLatido.current && t - ultimoLatido.current >= cadaMs) latir(t);
  };

  useEffect(() => {
    if (!activo) return undefined;
    const t0 = ahora();
    // Una pestaña que se abre con actividad reciente de otra la hereda; un valor viejo de una sesión anterior no cuenta (por eso el máximo con t0).
    ultima.current = Math.max(leerActividad() ?? 0, t0);
    guardarActividad(ultima.current);
    limite.current = limiteInicial;
    absolutoHasta.current = null;
    vencido.current = false;
    latir(t0); // abrir la pantalla es actividad, y trae los límites efectivos del servidor
    const alActuar = () => registrar.current();
    const alVolver = () => {
      if (document.visibilityState === 'visible') evaluar.current();
    };
    const alCambiarAlmacen = (e: StorageEvent) => {
      if (e.key !== CLAVE_CIERRE || !e.newValue || vencido.current) return;
      vencido.current = true;
      setEstado('vencido');
      cb.current.onVencido('otra-pestana');
    };
    for (const e of EVENTOS) window.addEventListener(e, alActuar, { passive: true });
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    window.addEventListener('storage', alCambiarAlmacen);
    const id = setInterval(() => evaluar.current(), intervaloMs);
    return () => {
      clearInterval(id);
      for (const e of EVENTOS) window.removeEventListener(e, alActuar);
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      window.removeEventListener('storage', alCambiarAlmacen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activo, limiteInicial, avisoMs, intervaloMs]);

  return {
    estado,
    segundos,
    /** Aviso del vencimiento absoluto (independiente del de inactividad) y los segundos que faltan. */
    absoluto,
    segundosAbsoluto,
    /** Cuenta como actividad (y avisa al servidor ya, para que la sesión viva aunque no haya otra petición). */
    seguir: () => {
      const t = ahora();
      ultima.current = t;
      guardarActividad(t);
      setEstado('activo');
      latir(t);
    },
  };
}
